import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight } from 'lucide-react';

const NAVY = '#0A2C93';

const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white pb-20 md:pb-0" data-testid="about-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-slate-100 px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate('/')} data-testid="about-back" className="p-1.5 rounded-lg hover:bg-slate-100">
            <ArrowLeft className="h-5 w-5 text-slate-600" />
          </button>
          <span className="font-logo text-xl font-black tracking-tight" style={{ color: NAVY }}>PlaySpaces</span>
        </div>
      </header>

      {/* Hero band */}
      <section className="bg-[#0A2C93] text-white px-4 py-14" data-testid="about-hero">
        <div className="max-w-3xl mx-auto">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-[#00D650] mb-3">
            About Us
          </span>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl leading-tight">
            Built by the people who run the venues
          </h1>
          <p className="mt-4 font-body text-sm sm:text-base text-white/70 leading-relaxed max-w-2xl">
            PlaySpaces is the easiest way to book sports venues in Bengaluru — from football turfs and box cricket
            to padel and pickleball — built and run by a team that lives and breathes the game.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="px-4 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-4 font-body text-sm sm:text-base text-[#4b5563] leading-relaxed">
            <p>
              PlaySpaces began behind the scenes. Before it was somewhere you could book a game, it was the
              operating system running our venues — the tool we built to make the day-to-day of managing sports
              facilities smoother. But the more time we spent running venues, the more we noticed the same thing:
              the players walking through our gates faced friction at every turn. Booking a slot meant phone calls
              and back-and-forth. Finding people to play with was hard. Knowing what coaching or events were
              happening was harder still.
            </p>
            <p>
              So we turned the tool outward. Today, PlaySpaces lets you do in a few taps what used to take a dozen
              phone calls — and it's built on the one thing most booking platforms don't have: we actually run the
              venues.
            </p>
          </div>

          <h2 className="font-heading font-bold text-lg sm:text-xl text-[#0A2C93] mt-10 mb-4">
            What you can do on PlaySpaces
          </h2>
          <ul className="space-y-3 font-body text-sm sm:text-base text-[#4b5563] leading-relaxed">
            <li className="flex gap-3">
              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#00D650] flex-shrink-0" />
              <span>
                <strong className="text-[#0A2C93]">Book a venue directly</strong> — see real-time availability,
                pick your sport and slot, and pay online in seconds. From 5-a-side football to full 11-a-side, box
                cricket, padel and pickleball.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#00D650] flex-shrink-0" />
              <span>
                <strong className="text-[#0A2C93]">Join a community game</strong> — don't have a full squad? Join
                an open game and play as an individual, and meet other players at the venue.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#00D650] flex-shrink-0" />
              <span>
                <strong className="text-[#0A2C93]">Join events</strong> — tournaments, leagues, and one-off events
                happening across our venues.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#00D650] flex-shrink-0" />
              <span>
                <strong className="text-[#0A2C93]">Book coaching</strong> — level up with structured coaching
                sessions run at the venue.
              </span>
            </li>
          </ul>

          <h2 className="font-heading font-bold text-lg sm:text-xl text-[#0A2C93] mt-10 mb-3">
            From one venue to seven
          </h2>
          <p className="font-body text-sm sm:text-base text-[#4b5563] leading-relaxed">
            We started in 2021 with a single venue — South United Football Club. Since then, PlaySpaces has grown to
            seven venues across Bengaluru, offering quality turf and courts, floodlights, and facilities built for
            players who take their game seriously — whether that's a weekly kickabout or a competitive league.
          </p>

          <h2 className="font-heading font-bold text-lg sm:text-xl text-[#0A2C93] mt-10 mb-3">
            Part of TRP Sports
          </h2>
          <p className="font-body text-sm sm:text-base text-[#4b5563] leading-relaxed">
            PlaySpaces is the venues vertical of TRP Sports, a sports management company built around a genuine love
            of the game. Alongside PlaySpaces, TRP Sports works across athlete management, sponsorship, social media
            management, and sports consulting — representing professional athletes and helping the wider sporting
            ecosystem grow. It means that when you book with PlaySpaces, you're playing at venues run by a team that
            lives and breathes sport at every level, from grassroots to professional.
          </p>

          <h2 className="font-heading font-bold text-lg sm:text-xl text-[#0A2C93] mt-10 mb-3">
            Why PlaySpaces
          </h2>
          <p className="font-body text-sm sm:text-base text-[#4b5563] leading-relaxed">
            Because we've stood on both sides of the gate. We know what it takes to run a great venue, and we know
            what players actually want — an open slot, a good surface, people to play with, and no hassle getting on
            the pitch. That's what we built PlaySpaces to deliver.
          </p>

          {/* CTA */}
          <div className="mt-12 rounded-2xl bg-slate-50 border border-slate-100 p-6 sm:p-8 text-center">
            <h2 className="font-heading font-bold text-xl text-[#0A2C93]">Ready to play?</h2>
            <p className="mt-2 font-body text-sm text-[#737373]">Find a venue near you and book your slot in seconds.</p>
            <button
              onClick={() => navigate('/book')}
              data-testid="about-book-cta"
              className="mt-5 inline-flex items-center gap-1.5 px-6 py-3 rounded-full text-white font-heading font-semibold text-sm hover:-translate-y-0.5 transition-transform"
              style={{ background: NAVY }}
            >
              Book a venue <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A2C93] text-white py-10 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <span className="font-logo text-2xl font-black">PlaySpaces</span>
          <div className="h-px w-16 mx-auto mt-2 mb-4 bg-white/20" />
          <p className="font-body text-sm text-white/50">&copy; 2026 PlaySpaces. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
